import React, { useMemo, useState } from "react";
import { PaeiChipRow, fmtRate, isNum } from "./paeiPrimitives";

// Who the optimizer had to choose from.
//
// The pool is assembled from the run itself: every holder on the AS-IS RACI is
// "staffed", and anyone the restaffing or the redesign moved work *to* who was not
// already holding a slot is "pool only". If the agent ever returns an explicit
// pool array, that wins — it will carry function and level, which the run's own
// rows do not.
export function buildJobPool(result) {
  const explicit =
    (Array.isArray(result?.job_pool) && result.job_pool) ||
    (Array.isArray(result?.jobs) && result.jobs) ||
    (Array.isArray(result?.pool) && result.pool) ||
    null;

  if (explicit) {
    return explicit.map((j) => ({
      key: String(j.job_id ?? j.name ?? j.job_name),
      job_id: j.job_id ?? null,
      name: j.job_name || j.name || "—",
      function: j.function || j.function_name || null,
      level: j.level || j.job_level || null,
      rate: j.hourlyRate ?? j.rate ?? j.hourly_rate ?? null,
      paei: j.paei || null,
      staffed: j.staffed !== false,
    }));
  }

  const byKey = new Map();
  const upsert = (key, patch) => {
    if (!key) return;
    const existing = byKey.get(key) || { key, job_id: null, name: key, function: null, level: null, rate: null, paei: null, staffed: false };
    byKey.set(key, {
      ...existing,
      ...patch,
      // Once a job is seen holding an AS-IS slot it is staffed, whatever a later
      // row says about it.
      staffed: existing.staffed || !!patch.staffed,
      rate: patch.rate != null ? patch.rate : existing.rate,
      paei: patch.paei || existing.paei,
      job_id: patch.job_id ?? existing.job_id,
    });
  };

  (result?.as_is?.tasks || []).forEach((task) => {
    (task.slots || []).forEach((s) => {
      const key = String(s.job_id ?? s.job_name ?? "");
      upsert(key, {
        job_id: s.job_id ?? null,
        name: s.job_name || key,
        rate: s.hourlyRate ?? null,
        paei: s.paei || null,
        staffed: true,
      });
    });
  });

  const addStaffingSide = (rows) => {
    (rows || []).forEach((r) => {
      if (r.from_job) {
        upsert(String(r.from_job), { name: r.from_job, rate: r.from_rate, paei: r.from_paei, staffed: true });
      }
      if (r.to_job) {
        upsert(String(r.to_job), { name: r.to_job, rate: r.to_rate, paei: r.to_paei });
      }
    });
  };
  addStaffingSide(result?.restaffing);
  addStaffingSide(result?.redesign?.staffing);

  return Array.from(byKey.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

function PaeiJobPoolTab({ result, coverage }) {
  const pool = useMemo(() => buildJobPool(result), [result]);
  const [query, setQuery] = useState("");

  const missing = coverage?.missing || result?.process?.pool_coverage?.missing || [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter((j) => String(j.name).toLowerCase().includes(q));
  }, [pool, query]);

  return (
    <div className="pa-panel">
      <p className="pa-card-hint">
        The candidates this run could reach. Coverage is <b>available to you</b>, never "assessed" —
        a job with no PAEI record and a job whose record your role may not see are indistinguishable
        from here.
      </p>

      <div className="pa-toolbar">
        <input
          type="search"
          className="pa-search"
          placeholder="Filter by job name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Filter the job pool"
        />
        <div className="pa-summary-strip inline">
          <span>
            <b>{pool.filter((j) => j.staffed).length}</b> staffed
          </span>
          <span>
            <b>{pool.filter((j) => !j.staffed).length}</b> pool only
          </span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="pa-empty">No jobs match.</div>
      ) : (
        <div className="pa-tbl-scroll">
          <table className="pa-tbl">
            <thead>
              <tr>
                <th>Job</th>
                <th>Function</th>
                <th>Level</th>
                <th className="pa-num">Rate</th>
                <th>PAEI</th>
                <th>In this run</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((j) => (
                <tr key={j.key}>
                  <td>
                    <div className="pa-step-name">{j.name}</div>
                    {j.job_id != null && <div className="pa-step-meta">JOB-{j.job_id}</div>}
                  </td>
                  <td>{j.function || <span className="pa-muted">—</span>}</td>
                  <td>{j.level || <span className="pa-muted">—</span>}</td>
                  <td className="pa-num">{isNum(j.rate) ? fmtRate(j.rate) : "—"}</td>
                  <td>
                    <PaeiChipRow values={j.paei} />
                  </td>
                  <td>
                    <span className={`pa-tag ${j.staffed ? "changed" : "idle"}`}>
                      {j.staffed ? "staffed" : "pool only"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {missing.length > 0 && (
        <section className="pa-card">
          <h3 className="pa-card-title">No PAEI profile available to you ({missing.length})</h3>
          <p className="pa-card-hint">
            The optimizer could not consider these jobs. Assessing them is the single fastest way to
            widen the pool — but their absence here does not mean they are unassessed, only that this
            run could not see a record.
          </p>
          <div className="pa-missing-list">
            {missing.map((m) => (
              <span key={m.job_id} className="pa-missing-chip">
                {m.name}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default PaeiJobPoolTab;
